import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
} from "@mui/material";
import ChatArea from "./ChatArea";
import ScoreboardArea from "./ScoreboardArea";
import CountdownTimer from "../CountdownTimer";
import { fireConfetti } from "../../utils/confetti";
import VoiceChat from "./VoiceChat";

const MANTRI_GUESS_TIME = 120; // 2 minutes in seconds

function GameArea({ roomCode, player, roomState, socket, setSystemMessage }) {
  const [mantriTimer, setMantriTimer] = useState(MANTRI_GUESS_TIME);
  const [showMantriGuessPrompt, setShowMantriGuessPrompt] = useState(false);
  const timerRef = useRef(null);
  const [roundResult, setRoundResult] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [gamePaused, setGamePaused] = useState(false);
  const [pauseReason, setPauseReason] = useState("");

  const getCurrentRole = () => {
    if (!roomState?.players || !player?.id) return null;
    const livePlayer = roomState.players.find((p) => p.id === player.id);
    return livePlayer?.role || player.role || null;
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    socket.on("gamePaused", ({ reason, remainingPlayers }) => {
      setGamePaused(true);
      setPauseReason(`${reason} (${remainingPlayers}/4 players connected)`);
      // Stop the timer when game is paused
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    });

    socket.on("gameResumed", () => {
      setGamePaused(false);
      setPauseReason("");
      // Timer will restart when startMantriGuessTimer is emitted again
    });

    socket.on("syncTimer", ({ remainingTime }) => {
      // Sync timer for reconnected players
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setMantriTimer(remainingTime);

      if (remainingTime > 0) {
        timerRef.current = setInterval(() => {
          setMantriTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              timerRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    });

    socket.on("gameStarted", (round) => {
      // setSystemMessage(`Round ${round} has started!`);
      setShowMantriGuessPrompt(false);
      setRoundResult(null);
    });

    // socket.on("yourRole", ({ role }) => {
    //   setSystemMessage(`You are the ${role}!`);
    // });

    socket.on("startMantriGuessTimer", ({ duration }) => {
      // CRITICAL: Clear any existing timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Reset to FULL duration (120 seconds)
      const timerDuration = duration || MANTRI_GUESS_TIME;
      setMantriTimer(timerDuration);

      // Start fresh countdown
      timerRef.current = setInterval(() => {
        setMantriTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // const currentRole = getCurrentRole();
      // if (currentRole === "Mantri") {
      //   setShowMantriGuessPrompt(true);
      //   setSystemMessage(
      //     "You are the Mantri! Click on the player you think is the Chor."
      //   );
      // } else if (currentRole === "Raja") {
      //   setSystemMessage("You are the Raja! Wait for Mantri's guess.");
      // } else if (currentRole === "Sipahi") {
      //   setSystemMessage("You are the Sipahi! Wait for Mantri's guess.");
      // } else if (currentRole === "Chor") {
      //   setSystemMessage("You are the Chor! Try to blend in...");
      // }
    });

    socket.on(
      "mantriGuessMade",
      ({ guesserUsername, guessedUsername, isCorrect }) => {
        // Stop timer immediately
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setShowMantriGuessPrompt(false);
        setMantriTimer(0);
        // setSystemMessage(
        //   `${guesserUsername} guessed ${guessedUsername}. It was ${
        //     isCorrect ? "CORRECT!" : "INCORRECT!"
        //   }`
        // );
      }
    );

    socket.on("mantriTimeOut", () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setShowMantriGuessPrompt(false);
      setMantriTimer(0);
      // setSystemMessage("Mantri did not make a guess in time!");
    });

    socket.on(
      "roundResult",
      ({ rolesAssigned, mantriGuess, finalScores, chorUsername }) => {
        setRoundResult({
          rolesAssigned,
          mantriGuess,
          finalScores,
          chorUsername,
        });
        // setSystemMessage(`Round ended! The Chor was ${chorUsername}.`);
        setShowMantriGuessPrompt(false);
      }
    );

    // socket.on("promptNextRound", () => {
    //   if (player.isHost) {
    //     setSystemMessage(
    //       'Round is over. Click "Next Round" to continue, or "End Game".'
    //     );
    //   } else {
    //     setSystemMessage(
    //       "Round is over. Waiting for host to start next round."
    //     );
    //   }
    // });

    return () => {
      socket.off("gameStarted");
      socket.off("yourRole");
      socket.off("startMantriGuessTimer");
      socket.off("mantriGuessMade");
      socket.off("mantriTimeOut");
      socket.off("roundResult");
      socket.off("promptNextRound");
      socket.off("gamePaused");
      socket.off("gameResumed");
      socket.off("syncTimer");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [socket, player, setSystemMessage]);

  const handleMantriGuess = (guessedPlayerId) => {
    setShowMantriGuessPrompt(false);
    socket.emit("mantriGuess", { roomCode, guessedPlayerId }, (response) => {
      if (response && !response.success) {
        alert(response.error || "Guess failed.");
        // Re-enable guess prompt if failed
        setShowMantriGuessPrompt(true);
      } else {
        // setSystemMessage("Guess submitted! Waiting for results...");
        console.log("Guess submitted!");
      }
    });
  };

  const handleNextRound = () => {
    socket.emit("nextRound", { roomCode });
  };

  const handleEndGame = () => {
    if (window.confirm("Are you sure you want to end the game?")) {
      socket.emit("endGame", { roomCode });
    }
  };

  useEffect(() => {
    if (roundResult?.mantriGuess?.isCorrect) {
      fireConfetti();
    }
  }, [roundResult]);

  if (!roomState) return <Typography>Loading game...</Typography>;

  const isMantriGuessPhase =
    roomState.status === "playing" &&
    roomState.mantriGuess &&
    !roomState.mantriGuess.hasGuessed;

  const currentRole = getCurrentRole();

  // Safety check
  if (!roomState || !player) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          color: "text.primary",
        }}
      >
        <Typography variant="h6">Loading game...</Typography>
      </Box>
    );
  }

  if (gamePaused)
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.7)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          color: "white",
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          Game Paused
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          {pauseReason}
        </Typography>
        <Typography variant="body2">
          Waiting for players to reconnect...
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 1,
        boxSizing: "border-box",
      }}
    >
      {/* Main Responsive Layout */}
      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Players */}
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}
            >
              Players
            </Typography>
            {roomState?.status === "waiting" ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: 150,
                }}
              >
                <Typography variant="body1">
                  {roomState.players.length < 4
                    ? "Waiting for all players to join the game."
                    : "All players have joined the game. Host will start the game."}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gridTemplateRows: "repeat(2, 1fr)",
                  gap: 2,
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                }}
              >
                {roomState.players.map((p) => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    isYou={p.id === player.id}
                    isRevealed={roundResult?.rolesAssigned[p.role] === p.id}
                    isGuessed={roundResult?.mantriGuess?.guessedChorId === p.id}
                    showGuess={
                      isMantriGuessPhase &&
                      roomState.rolesAssigned.Mantri === player.id &&
                      p.id !== player.id &&
                      p.connected
                    }
                    onGuess={handleMantriGuess}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Scoreboard */}
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <ScoreboardArea players={roomState.players} />
            <RoundSummary
              player={player}
              roundResult={roundResult}
              handleNextRound={handleNextRound}
              handleEndGame={handleEndGame}
              isMantriGuessPhase={isMantriGuessPhase}
              currentRole={currentRole}
              mantriTimer={mantriTimer}
              theme={theme}
            />
          </Paper>

          {/* Chat */}
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
              height: 400,
              display: "flex",
              flexDirection: "column",
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <ChatArea
              roomCode={roomCode}
              playerUsername={player.username}
              socket={socket}
              messages={roomState.messages || []}
            />
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <VoiceChat
                socket={socket}
                roomCode={roomCode}
                players={roomState.players}
              />
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            height: "calc(100vh - 200px)",
          }}
        >
          {/* Scoreboard */}
          <Paper
            sx={{
              width: "30%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <ScoreboardArea players={roomState.players} />
            <RoundSummary
              player={player}
              roundResult={roundResult}
              handleNextRound={handleNextRound}
              handleEndGame={handleEndGame}
              isMantriGuessPhase={isMantriGuessPhase}
              currentRole={currentRole}
              mantriTimer={mantriTimer}
              theme={theme}
            />
          </Paper>

          {/* Players */}
          <Paper
            sx={{
              width: "40%",
              p: 2,
              borderRadius: 2,
              boxShadow: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              flex: 1,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}
            >
              Players
            </Typography>
            {roomState?.status === "waiting" ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: 150,
                }}
              >
                <Typography variant="body1">
                  {roomState.players.length < 4
                    ? "Waiting for all players to join the game."
                    : "All players have joined the game. Host will start the game."}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gridTemplateRows: "repeat(2, 1fr)",
                  gap: 2,
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {roomState.players.map((p) => (
                  <PlayerCard
                    key={p.id}
                    player={p}
                    isYou={p.id === player.id}
                    isRevealed={roundResult?.rolesAssigned[p.role] === p.id}
                    isGuessed={roundResult?.mantriGuess?.guessedChorId === p.id}
                    showGuess={
                      isMantriGuessPhase &&
                      roomState.rolesAssigned.Mantri === player.id &&
                      p.id !== player.id &&
                      p.connected
                    }
                    onGuess={handleMantriGuess}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Chat */}
          <Paper
            sx={{
              width: "30%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <ChatArea
              roomCode={roomCode}
              playerUsername={player.username}
              socket={socket}
              messages={roomState.messages || []}
            />
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <VoiceChat
                socket={socket}
                roomCode={roomCode}
                players={roomState.players}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Bottom Controls */}
      <Box sx={{ mt: 5 }}>
        {!roundResult && roomState.currentRound === 0 && player.isHost && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => socket.emit("startGame", { roomCode })}
              disabled={roomState.players.length < 4}
              sx={{
                py: 1.5,
                borderRadius: 2,
                boxShadow: 1,
                "&.Mui-disabled": {
                  backgroundColor: "#3d4247ff",
                  color: "#ccc",
                },
              }}
            >
              {roomState.players.length < 4
                ? `Waiting for ${4 - roomState.players.length} more players...`
                : "Start Game"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const PlayerCard = ({
  player,
  isYou,
  isRevealed,
  isGuessed,
  showGuess,
  onGuess,
}) => {
  const theme = useTheme();
  const isDisconnected = !player.connected;

  return (
    <Paper
      elevation={isGuessed ? 6 : 3}
      onClick={() => showGuess && !isDisconnected && onGuess(player.id)}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: showGuess && !isDisconnected ? "pointer" : "default",
        transition: "transform 0.2s",
        border: isYou
          ? `2px solid ${theme.palette.primary.main}`
          : "1px solid transparent",
        opacity: isDisconnected ? 0.5 : 1,
        filter: isDisconnected ? "grayscale(80%)" : "none",
        position: "relative",
        backgroundColor: isRevealed
          ? theme.palette.success.dark
          : theme.palette.background.paper,
        "&:hover": {
          transform: showGuess && !isDisconnected ? "scale(1.03)" : "none",
          boxShadow: showGuess && !isDisconnected ? 4 : 2,
        },
        borderRadius: 2,
        width: "100%",
        height: "100%",
        color: theme.palette.text.primary,
      }}
    >
      {isDisconnected && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: 8,
            fontWeight: "bold",
            color: "error.main",
            backgroundColor: "rgba(0,0,0,0.5)",
            px: 1,
            borderRadius: 1,
          }}
        >
          DISCONNECTED
        </Typography>
      )}
      <Avatar
        sx={{
          width: 64,
          height: 64,
          mb: 1,
          fontSize: "1.5rem",
          bgcolor: isRevealed
            ? theme.palette.success.main
            : theme.palette.grey[700],
        }}
      >
        {player.username.charAt(0).toUpperCase()}
      </Avatar>
      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "center" }}>
        {player.username} {isYou && "(You)"}
      </Typography>

      {isRevealed && (
        <Chip
          label={player.role}
          size="small"
          sx={{ mt: 1 }}
          color={
            player.role === "Raja"
              ? "warning"
              : player.role === "Mantri"
              ? "info"
              : player.role === "Chor"
              ? "error"
              : "success"
          }
        />
      )}
    </Paper>
  );
};

const RoundSummary = ({
  roundResult,
  player,
  handleNextRound,
  handleEndGame,
  isMantriGuessPhase,
  currentRole,
  mantriTimer,
  theme,
}) => {
  if (!roundResult) {
    return isMantriGuessPhase && mantriTimer > 0 ? (
      <CountdownTimer
        initialSeconds={mantriTimer}
        message={
          currentRole === "Mantri"
            ? "Time left to guess"
            : "Mantri is guessing..."
        }
      />
    ) : null;
  }

  return (
    <Box sx={{ py: 1, px: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}
      >
        Round Summary
      </Typography>

      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: 2,
          p: 1,
          mb: 1,
        }}
      >
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Chor:</strong> {roundResult.chorUsername}
        </Typography>

        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Mantri's guess:</strong>{" "}
          {roundResult.mantriGuess?.hasGuessed
            ? roundResult.mantriGuess.isCorrect
              ? "Correct ✅"
              : "Incorrect ❌"
            : "Mantri did not make a guess."}
        </Typography>

        {roundResult.mantriGuess?.guessedChorId === "NoGuess" && (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Mantri did not guess in time.
          </Typography>
        )}
      </Box>

      {player?.isHost && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            size="medium"
            onClick={handleNextRound}
            sx={{ px: 3, py: 1 }}
          >
            Next Round
          </Button>
          <Button
            variant="outlined"
            size="medium"
            color="error"
            onClick={handleEndGame}
            sx={{ px: 3, py: 1 }}
          >
            End Game
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GameArea;
